import React, { useEffect, useMemo, useRef } from "react";
import { useState } from "react";
import { useDispatch } from "react-redux";
import Loader from "../../../../components/common/Loader";
import { bulkCustomerRecharge } from "../../../../features/actions/bulkOperationApi";
import { useTranslation } from "react-i18next";
import { Field, Form, Formik } from "formik";
import { FtextField } from "../../../../components/common/FtextField";
import useDataInputOption from "../../../../hooks/useDataInputOption";
import useISPowner from "../../../../hooks/useISPOwner";
import useSelectorState from "../../../../hooks/useSelectorState";
import ComponentCustomModal from "../../../../components/common/customModal/ComponentCustomModal";
import Table from "../../../../components/table/Table";
import { Trash } from "react-bootstrap-icons";
import { ListGroup } from "react-bootstrap";

const BulkRecharge = ({ bulkCustomer, show, setShow, page }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  // get user & current user data form useISPOwner hook
  const { userData, currentUser } = useISPowner();

  //---> Get redux store state data from useSelectorState hooks
  const { allPackages, withoutMtkPackages } = useSelectorState();

  const extraInputOptions = [
    {
      name: "billingType",
      as: "select",
      type: "select",
      id: "billingType",
      isVisible: true,
      disabled: false,
      validation: true,
      label: t("billingType"),
      textAccessor: "name",
      valueAccessor: "value",
      options: [
        {
          value: "monthly",
          name: t("monthly"),
        },
        {
          value: "daily",
          name: t("daily"),
        },
      ],
    },
    {
      name: "dayLength",
      type: "text",
      id: "dayLength",
      visibleKey: "billingType",
      visibleVlaue: "daily",
      disabled: false,
      validation: false,
      label: t("dayLength"),
    },
  ];

  // call the data input option function
  const inputPermission = {
    billingType: true,
    dayLength: true,
  };

  // get data input option from useDataInputOption hook
  const dataInputOption = useDataInputOption(
    inputPermission,
    page,
    null,
    null,
    extraInputOptions,
  );

  const visibleInputFields = useMemo(() => {
    if (!dataInputOption?.inputOption) return [];

    const optionMap = dataInputOption.inputOption.reduce((acc, option) => {
      acc[option.name] = option;
      return acc;
    }, {});

    return Object.keys(inputPermission)
      .map((field) => optionMap[field])
      .filter((item) => item && item.isVisible);
  }, [dataInputOption, inputPermission]);

  // Loading state
  const [isLoading, setIsLoading] = useState(false);
  const [billingType, setBillingType] = useState("");
  const [rechargeCustomer, setRechargeCustomers] = useState([]);

  useEffect(() => {
    if (bulkCustomer) {
      const modifiedCustomers = [];

      for (const option of bulkCustomer) {
        if (option?.original.monthlyFee > 0) {
          modifiedCustomers.push({
            ...option?.original,
            amount: option?.original.monthlyFee,
          });
        }
      }
      setRechargeCustomers(modifiedCustomers);
    }
  }, [bulkCustomer]);

  const compearCustomers = (billingType, dayLength) => {
    const modifiedCustomers = [];

    for (const customer of rechargeCustomer) {
      let monthlyBill = customer?.monthlyFee;

      if (billingType === "daily") {
        const date = new Date();
        const monthDate = new Date(
          date.getFullYear(),
          date.getMonth() + 1,
          0,
        ).getDate();

        const customerPerDayBill = customer?.monthlyFee / monthDate;

        const dayTodayFee = Math.round(customerPerDayBill * dayLength);
        monthlyBill = dayTodayFee;
      }

      modifiedCustomers.push({
        ...customer,
        dayLength: dayLength,
        amount: Math.trunc(monthlyBill),
      });
    }

    setRechargeCustomers(modifiedCustomers);
  };

  const handleFormValue = (event, values, setFieldValue) => {
    const { mikrotikPackage, billingType, dayLength } = values;

    const mtkPackage = allPackages?.find((pack) => pack.id === mikrotikPackage);

    if (event.target.name === "billingType") {
      setBillingType(event.target.value);

      setFieldValue("billingType", event.target.value);
      if (event.target.value === "daily") {
        setFieldValue("dayLength", 1);

        compearCustomers(event.target.value, 1);
      } else {
        setFieldValue("monthlyFee", mtkPackage?.rate);
        compearCustomers(event.target.value);
      }
    }

    if (event.target.name === "dayLength") {
      setFieldValue("dayLength", Number(event.target.value));

      compearCustomers(billingType, Number(event.target.value));
    }

    if (event.target.name === "mikrotikPackage" && billingType === "daily") {
      const singlePakcage = allPackages?.find(
        (pack) => pack.id === event.target.value,
      );

      const date = new Date();
      const monthDate = new Date(
        date.getFullYear(),
        date.getMonth() + 1,
        0,
      ).getDate();
      const customerPerDayBill = singlePakcage?.rate / monthDate;

      const dayTodayFee = customerPerDayBill * dayLength;
      setFieldValue("monthlyFee", Math.trunc(dayTodayFee));
    }
  };

  const removeSelectedUserFromBulkRechargeOptions = (id) => {
    const remainingCustomers = rechargeCustomer.filter(
      (customoer) => customoer?.id !== id,
    );

    setRechargeCustomers(remainingCustomers);
  };

  const customerPackageFind = (pack) => {
    const findPack = allPackages.find((item) => item.id.includes(pack));
    return findPack;
  };

  // Bulk customer recharge form handler function
  const customerBulkRechargeHandler = (formValue) => {
    const { medium, billingType, dayLength } = formValue;

    const customers = rechargeCustomer.map((customer) => {
      return {
        id: customer.id,
        amount: customer.amount,
        billingType,
        dayLength,
        medium,
      };
    });

    const data = {
      customerIds: customers,
      name: userData.name,
      collectedBy: currentUser?.user.role,
      user: currentUser?.user.id,
      collectorId: userData?.id,
    };

    const confirm = window.confirm(t("bulkCustomerRechargeConfirmation"));
    if (confirm) {
      bulkCustomerRecharge(dispatch, data, setIsLoading, setShow);
    }
  };

  const columns = useMemo(
    () => [
      {
        width: "14%",
        Header: t("namePPPoE"),
        accessor: (data) => `${data?.name} ${data?.pppoe?.name}`,
        Cell: ({ row: { original } }) => (
          <div>
            <p>{original?.name}</p>
            <p>
              {original?.userType === "pppoe"
                ? original?.pppoe?.name
                : original?.queue?.name}
            </p>
          </div>
        ),
      },
      {
        width: "14%",
        Header: t("packageBill"),
        accessor: (field) =>
          field.customer?.userType === "pppoe"
            ? field.customer?.pppoe?.profile
            : customerPackageFind(
                field?.uploadPackage
                  ? field?.uploadPackage
                  : field?.mikrotikPackage,
              )?.name,
        Cell: ({ row: { original } }) => {
          return (
            <div>
              <p>
                {original?.userType === "pppoe" ? (
                  original?.pppoe.profile
                ) : (
                  <p>
                    {
                      customerPackageFind(
                        original?.uploadPackage
                          ? original?.uploadPackage
                          : original?.mikrotikPackage,
                      )?.name
                    }
                    /
                    {
                      customerPackageFind(
                        original?.downloadPackage
                          ? original?.downloadPackage
                          : original?.mikrotikPackage,
                      )?.name
                    }
                  </p>
                )}
              </p>
              <p style={{ fontWeight: "500" }}>৳{original?.monthlyFee}</p>
            </div>
          );
        },
      },

      ...(billingType === "daily"
        ? [
            {
              width: "11%",
              Header: t("dayLength"),
              accessor: "dayLength",
            },
          ]
        : []),

      {
        width: "11%",
        Header: t("amount"),
        accessor: "amount",
      },

      {
        width: "5%",
        Header: t("action"),
        id: "option",
        Cell: ({ row: { original } }) => (
          <div className="d-flex justify-content-center align-items-center">
            <button
              type="button"
              className="btn btn-sm text-danger text-center"
              onClick={() =>
                removeSelectedUserFromBulkRechargeOptions(original.id)
              }
            >
              <Trash size={18} />
            </button>
          </div>
        ),
      },
    ],
    [t, billingType, rechargeCustomer],
  );

  return (
    <ComponentCustomModal
      size="lg"
      show={show}
      setShow={setShow}
      header={t("bulkRecharge")}
    >
      <Formik
        initialValues={{
          billingType: "monthly",
          medium: "cash",
        }}
        onSubmit={(values) => {
          customerBulkRechargeHandler(values);
        }}
        enableReinitialize
      >
        {({ values, setFieldValue }) => (
          <Form
            onChange={(event) => handleFormValue(event, values, setFieldValue)}
            id="packageRecharge"
          >
            <div className="displayGrid2">
              {visibleInputFields?.map(
                (item) =>
                  item?.isVisible && <FtextField key={item.name} {...item} />,
              )}

              <div>
                <label className="form-control-label changeLabelFontColor">
                  {t("medium")} <span className="text-danger">*</span>
                </label>
                <Field
                  as="select"
                  name="medium"
                  className="form-select mt-0 mw-100"
                >
                  <option value="cash">{t("handCash")}</option>
                  <option value="bKash">{t("bKash")}</option>
                  <option value="rocket">{t("rocket")}</option>
                  <option value="nagad">{t("nagad")}</option>
                  <option value="others">{t("others")}</option>
                </Field>
              </div>
            </div>
          </Form>
        )}
      </Formik>

      <div className="collectorWrapper pb-2">
        <Table columns={columns} data={rechargeCustomer} />
      </div>

      <div className="modal-footer" style={{ border: "none" }}>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={isLoading}
          onClick={() => setShow(false)}
        >
          {t("cancel")}
        </button>
        <button
          type="submit"
          className="btn btn-success"
          form="packageRecharge"
          disabled={isLoading}
        >
          {isLoading ? <Loader /> : t("recharge")}
        </button>
      </div>
    </ComponentCustomModal>
  );
};

export default BulkRecharge;
